<mat-card
  class="frame"
  appearance="outlined"
  [class.frame-bare]="!isCard()"
  [class.frame-placeholder]="placeholderCard()"
  [class.frame-editing]="editing()"
  [attr.aria-label]="isCard() ? null : title()">
  @if (isCard()) {
    <mat-card-header class="frame-header">
      <mat-card-title class="frame-title">{{ title() }}</mat-card-title>
      @if (subtitle(); as subtitleText) {
        <mat-card-subtitle class="frame-subtitle">{{ subtitleText }}</mat-card-subtitle>
      }

      <div class="frame-actions">
        @if (headerActions(); as actions) {
          <ng-container [ngTemplateOutlet]="actions" />
        }

        @if (editing()) {
          <ng-container [ngTemplateOutlet]="editControls" />
        }
      </div>
    </mat-card-header>
  } @else if (editing()) {
    <div class="frame-floating-controls">
      <ng-container [ngTemplateOutlet]="editControls" />
    </div>
  }

  <mat-card-content class="frame-body">
    @let current = view();
    @if (body.state(); as code) {
      @if (code.status === 'ready') {
        <div
          class="frame-content"
          animate.enter="frame-reveal"
          [class.frame-content-covered]="bodyHidden()"
          [attr.aria-busy]="current.kind === 'first-load' || current.kind === 'refreshing'">
          <ng-container [ngComponentOutlet]="code.component" [ngComponentOutletInjector]="bodyInjector()" />
        </div>
      }
    }

    @switch (current.kind) {
      @case ('code-loading') {
        <ng-container
          [ngTemplateOutlet]="loadingIndicator"
          [ngTemplateOutletContext]="{ $implicit: 'Dashboard.Frame.LoadingWidget' | translate: locale.language }" />
      }
      @case ('first-load') {
        <ng-container
          [ngTemplateOutlet]="loadingIndicator"
          [ngTemplateOutletContext]="{ $implicit: 'Dashboard.Frame.LoadingData' | translate: locale.language }" />
      }
      @case ('code-failed') {
        <div class="frame-message frame-failed" role="alert" animate.enter="frame-fade-in">
          <mat-icon class="frame-message-icon" aria-hidden="true">error_outline</mat-icon>
          <p>{{ current.message | translate: locale.language }}</p>
          <button mat-stroked-button type="button" (click)="body.retry()">
            {{ 'Dashboard.Frame.TryAgain' | translate: locale.language }}
          </button>
        </div>
      }
      @case ('failed') {
        <div
          class="frame-message frame-overlay frame-failed"
          role="alert"
          animate.enter="frame-fade-in"
          animate.leave="frame-fade-out">
          <mat-icon class="frame-message-icon" aria-hidden="true">error_outline</mat-icon>
          <p>{{ current.message | translate: locale.language }}</p>
          @if (current.retry; as retry) {
            <button mat-stroked-button type="button" (click)="retry()">
              {{ 'Dashboard.Frame.TryAgain' | translate: locale.language }}
            </button>
          }
        </div>
      }
      @case ('empty') {
        <output class="frame-message frame-overlay" animate.enter="frame-fade-in" animate.leave="frame-fade-out">
          <mat-icon class="frame-message-icon" aria-hidden="true">inbox</mat-icon>
          <span>{{ current.message | translate: locale.language }}</span>
        </output>
      }
      @case ('unavailable') {
        <div class="frame-message">
          <p>{{ 'Dashboard.Frame.Unavailable' | translate: locale.language }}</p>
        </div>
      }
    }
  </mat-card-content>
</mat-card>

<ng-template #loadingIndicator let-label>
  <div class="frame-loading" animate.enter="frame-loading-in" animate.leave="frame-fade-out">
    @if (indicatorShape() === 'spinner') {
      <mat-progress-spinner mode="indeterminate" diameter="40" [attr.aria-label]="label" />
    } @else {
      <mat-progress-bar class="frame-loading-bar" mode="indeterminate" [attr.aria-label]="label" />
    }
  </div>
</ng-template>

<ng-template #editControls>
  <button
    #dragHandle
    mat-icon-button
    type="button"
    class="frame-control dashboard-drag-handle"
    [attr.aria-label]="dragHandleLabel()"
    (keydown)="onDragHandleKeydown($event)">
    <mat-icon>drag_indicator</mat-icon>
  </button>
  @if (hasSettings()) {
    <button
      mat-icon-button
      type="button"
      class="frame-control"
      [attr.aria-label]="'Dashboard.Frame.EditSettings' | translate: locale.language : { title: title() }"
      (click)="settingsRequested.emit()">
      <mat-icon>settings</mat-icon>
    </button>
  }

  <button
    mat-icon-button
    type="button"
    class="frame-control"
    [attr.aria-label]="'Dashboard.Frame.Remove' | translate: locale.language : { title: title() }"
    (click)="removeRequested.emit()">
    <mat-icon>close</mat-icon>
  </button>
</ng-template>
